/**
 * Live tracking, the staff live map of Teslyn Cloud (decision 842: "live tracking is for staff and needs to work like
 * the maps in Teslyn Field"; docs/specs/cloud/live-tracking.md). The page draws one project at a time with the layers
 * every other Cloud map already draws (`blockFeatures`, `lineStateFeatures`, `linesRemaining`, `landholderFeatures`,
 * the hazard pins, the pilot hazard pack, `hubTracking`, the fire layers); this module holds only what is the page's
 * own: which projects it offers and which first, the layer ticks and their defaults (Teslyn Field's Map defaults,
 * docs/specs/map.md §4), a pin of the pack drawn once, and what the replay covers.
 */
import type { AircraftPosition } from './aircraft-layer.js';
import { CLOUD_TRAFFIC_STATUSES } from './cloud-traffic.js';
import type { PortalHazardLayer } from './portal-hazards.js';
import { projectStatusLabel } from './project-lifecycle.js';
import { REPLAY_HOURS } from './tracker-replay.js';

// ---------------------------------------------------------------------------------------------------- the layers

export type LiveLayerId =
  'boundaries' | 'lines' | 'remaining' | 'landholders' | 'pack' | 'pins' | 'shapes' | 'aircraft' | 'traffic';

export interface LiveLayerDef {
  readonly id: LiveLayerId;
  readonly word: string;
  /** Said under the tick (what it is, or where it comes from). */
  readonly note?: string;
}

/** The ticks of the Layers panel, in the order they are listed (Teslyn Field's order, bottom layer first). */
export const LIVE_LAYERS: readonly LiveLayerDef[] = [
  { id: 'boundaries', word: 'Block boundaries' },
  { id: 'lines', word: 'Line states' },
  { id: 'remaining', word: 'Lines remaining', note: 'Drawn in place of the line states while ticked.' },
  { id: 'landholders', word: 'Landholder properties' },
  { id: 'pack', word: 'Hazard pack', note: 'The current pack from Teslyn Field.' },
  { id: 'pins', word: 'Hazard pins' },
  // MB's R152, decision 921
  {
    id: 'shapes',
    word: 'Shape file layers',
    note: 'Hazards and other layers added from shape files in Teslyn Field.',
  },
  { id: 'aircraft', word: 'Our aircraft and vehicles' },
  { id: 'traffic', word: 'Public air traffic' },
];

export type LiveLayers = Readonly<Record<LiveLayerId, boolean>>;

/** Teslyn Field's Map defaults (docs/specs/map.md §4): everything on but the lines remaining. */
export const LIVE_LAYER_DEFAULTS: LiveLayers = {
  boundaries: true,
  lines: true,
  remaining: false,
  landholders: true,
  pack: true,
  pins: true,
  shapes: true,
  aircraft: true,
  traffic: true,
};

/** The ticks kept in this browser, read back: only known ticks, each a boolean; anything else is the default. */
export function readLiveLayers(stored: unknown): LiveLayers {
  const v = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
  const out: Record<LiveLayerId, boolean> = { ...LIVE_LAYER_DEFAULTS };
  for (const l of LIVE_LAYERS) {
    const x = v[l.id];
    if (typeof x === 'boolean') out[l.id] = x;
  }
  return out;
}

/** Which lines are drawn (and so which legend shows): the line states, the lines remaining in their place, or none. */
export function liveLinesShown(layers: LiveLayers): 'states' | 'remaining' | null {
  if (layers.remaining) return 'remaining';
  return layers.lines ? 'states' : null;
}

// ---------------------------------------------------------------------------------------------- the project chooser

export interface LiveProjectRow {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly status: string;
}

/** Active first (the crews are flying it), then mobilising, planned and complete; archived projects are not offered. */
const STATUS_ORDER: readonly string[] = ['active', 'mobilising', 'planned', 'complete'];

/** The projects the chooser offers, in its order: by status, then by number. */
export function liveProjectChoices<T extends LiveProjectRow>(projects: readonly T[]): T[] {
  return projects
    .filter((p) => STATUS_ORDER.includes(p.status))
    .sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        a.number.localeCompare(b.number, 'en-AU', { numeric: true }) ||
        a.id.localeCompare(b.id),
    );
}

/** The project to show: the one this browser last chose while it is still offered, else the first offered. */
export function liveDefaultProject(
  choices: readonly LiveProjectRow[],
  remembered: string | null,
): string | null {
  if (remembered && choices.some((p) => p.id === remembered)) return remembered;
  return choices[0]?.id ?? null;
}

/** A project in the chooser: "F26048 Milgun · Active". */
export function liveProjectWords(p: LiveProjectRow): string {
  return `${p.number} ${p.title} · ${projectStatusLabel(p.status)}`;
}

/** Public air traffic is asked only around projects mobilising or active (the poller's areas). */
export function liveTrafficAsked(status: string): boolean {
  return CLOUD_TRAFFIC_STATUSES.includes(status);
}

/** Said under the map for a project the poller does not ask about; null when it does. */
export function liveTrafficNotAskedWords(status: string): string | null {
  return liveTrafficAsked(status)
    ? null
    : `Public air traffic is asked only around projects that are mobilising or active; this one is ${projectStatusLabel(status).toLowerCase()}.`;
}

// ------------------------------------------------------------------------------------------ pins of the hazard pack

/** A pin of the pack within this of a hazard pin record of the same name (about 5 m) is that record. */
export const PACK_PIN_SAME_DEG = 5e-5;

/**
 * The pilot hazard pack's layers without the pins that are also hazard pin records (same name, within about 5 m):
 * the pack holds every pin as it was when published (docs/specs/map.md §33.2), the records are the pins as the crews
 * keep them now, so each pin is drawn once, from its record. A pin moved or renamed since the pack was published is
 * drawn twice on purpose: once where the pilots have it, once where it is now.
 */
/**
 * The pack without the shape file hazards the map draws from their records (decision 921): the pack's
 * "Shape file: <name>" folder of a layer the project still has is left out, so a layer is drawn once.
 */
export function packWithoutShapes(
  layers: readonly PortalHazardLayer[],
  shapes: readonly { readonly name: string }[],
): PortalHazardLayer[] {
  const names = new Set(shapes.map((s) => `Shape file: ${s.name}`));
  return layers.filter((l) => !names.has(l.name));
}

export function packWithoutPins(
  layers: readonly PortalHazardLayer[],
  pins: readonly { readonly name: string; readonly lat: number; readonly lng: number }[],
): PortalHazardLayer[] {
  if (pins.length === 0) return [...layers];
  const isPin = (name: string, lat: number, lng: number): boolean =>
    pins.some(
      (p) =>
        p.name.trim() === name.trim() &&
        Math.abs(p.lat - lat) <= PACK_PIN_SAME_DEG &&
        Math.abs(p.lng - lng) <= PACK_PIN_SAME_DEG,
    );
  return layers
    .map((l) => ({
      ...l,
      placemarks: l.placemarks.filter(
        (m) => !(m.shape.type === 'point' && isPin(m.name, m.shape.at.lat, m.shape.at.lng)),
      ),
    }))
    .filter((l) => l.placemarks.length > 0);
}

// ---------------------------------------------------------------------------------------------------- the replay

/** The history read while the replay is open: all Teslyn Cloud keeps of the organisation's trackers (decision 75). */
export const LIVE_REPLAY_HOURS = REPLAY_HOURS;

/** The history to start with: Teslyn Field's and the Operations Hub's default. */
export const LIVE_HISTORY_HOURS = 12;

/** The aircraft a replay plays: the organisation's own (public air traffic is kept 2 hours only, decision 825). */
export function liveReplayAircraft<T extends Pick<AircraftPosition, 'fleet'>>(aircraft: readonly T[]): T[] {
  return aircraft.filter((a) => a.fleet !== false);
}

/** Said in the replay bar. */
export const LIVE_REPLAY_NOTE =
  'Replaying the last 48 hours Teslyn Cloud keeps: each of our aircraft at its last position at that time. Public air traffic is not replayed (Teslyn Cloud keeps 2 hours of it).';

// ------------------------------------------------------------------------------------------------------ refreshes

/** Line states, pins and landholder records are read again this often while the page is open. */
export const LIVE_RECORDS_REFRESH_MS = 120_000;
